/** The little payoff picture on a strategy card.
 *
 * ⭐ A SHAPE, NOT A QUOTE — and this is the whole design decision. The
 * thumbnail answers "which structure is this?" at a glance, and for that
 * the CHARACTERISTIC OUTLINE is the entire content: a tent, a V, a
 * flat-topped table, twin peaks. Nothing about it should move when the
 * clock does.
 *
 * Pricing it off the real chain was the obvious alternative and is worse
 * on every count. It would redraw fourteen thumbnails on every clock step
 * for no information a reader could use at that size; it would render
 * nothing at all for the templates the archive cannot build, which is most
 * of them on a pre-2026-08-08 session, leaving a grid of blank cards; and
 * a picture too small to read a number off is exactly the wrong place to
 * imply real prices. The card already lists its real strikes, and the real
 * payoff is one click away at full size.
 *
 * So the premiums here are a deliberately crude synthetic: intrinsic at
 * the money plus a bell of time value that decays with distance from it.
 * That is enough to put every structure's kink in the right place with the
 * right sign, which is all the outline is made of. It is NOT a pricing
 * model and must never be used as one.
 */

import type { TemplateLeg } from "./strategyTemplates";

/** Width of the time-value bell, in strikes. Two makes an ATM option
 * clearly the richest and a 4-out wing nearly worthless — the proportions
 * a reader expects to see in the shape. */
const SIGMA = 2;
/** How far either side of the money the picture runs, in strikes. Wide
 * enough that a condor's outer wings are inside the frame at width 2. */
const SPAN = 7;
/** Samples across the span. Kinks land on strikes, which are integers, so
 * a step of 0.25 puts a vertex on every one of them. */
const STEP = 0.25;

/** What one leg is worth at expiry, in strike units, when spot lands at
 * `s`. Offsets and spot are both measured from the money. */
function intrinsic(leg: TemplateLeg, s: number): number {
  return leg.side === "CE" ? Math.max(0, s - leg.offset) : Math.max(0, leg.offset - s);
}

/** Synthetic premium: what the leg costs at the money. */
function premium(leg: TemplateLeg): number {
  const timeValue = Math.exp(-(leg.offset * leg.offset) / (2 * SIGMA * SIGMA));
  return intrinsic(leg, 0) + timeValue;
}

/** Net P&L at expiry for the basket, at spot `s` strikes from the money. */
export function payoffAt(legs: TemplateLeg[], s: number): number {
  return legs.reduce((total, leg) => {
    const sign = leg.direction === "BUY" ? 1 : -1;
    return total + sign * leg.lots * (intrinsic(leg, s) - premium(leg));
  }, 0);
}

export interface ShapePoint {
  /** 0 at the left edge, 1 at the right. */
  x: number;
  /** 0 at the top, 1 at the bottom — SVG's direction, so the caller can
   * multiply by height and draw. */
  y: number;
}

export interface Shape {
  points: ShapePoint[];
  /** Where P&L zero sits, on the same 0..1 top-to-bottom scale. Profit is
   * ABOVE this line and loss below, which is what the two fills clip to. */
  zero: number;
}

/** Sample the structure into a drawable outline, scaled to fit.
 *
 * ⚠️ The vertical scale is SYMMETRIC about zero — the largest excursion in
 * either direction sets both halves. Scaling each half independently would
 * make a 1-unit profit and a 40-unit loss look the same size, which is the
 * one thing a risk picture must never do.
 */
export function shapeOf(legs: TemplateLeg[]): Shape {
  const raw: { s: number; pnl: number }[] = [];
  for (let s = -SPAN; s <= SPAN; s += STEP) raw.push({ s, pnl: payoffAt(legs, s) });

  const extent = Math.max(...raw.map((p) => Math.abs(p.pnl)), 1e-6);
  // A tenth of headroom so a flat top does not sit exactly on the frame.
  const scale = extent * 1.1;
  return {
    zero: 0.5,
    points: raw.map((p) => ({
      x: (p.s + SPAN) / (2 * SPAN),
      y: 0.5 - p.pnl / (2 * scale),
    })),
  };
}
